/**
 * WCAG 2.x contrast helpers for the Encuentro tokens (Alumnia UI 1.2.1).
 *
 * Ported from the artifact's `bundle.src.js` (institution accent guard) so the
 * server computes exactly what the design system computes: same luminance
 * formula, same 0.03928 threshold and the same rounding in `mix`.
 */

const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i

export type Rgb = readonly [number, number, number]

/** True for `#rgb` or `#rrggbb` (the `#` is required). */
export function isHexColor(value: unknown): value is string {
  return typeof value === 'string' && value.startsWith('#') && HEX_RE.test(value)
}

export function hexToRgb(hex: string): Rgb {
  const match = HEX_RE.exec(hex.trim())
  if (!match) throw new Error(`Not a hex color: ${hex}`)
  let h = match[1]
  if (h.length === 3) h = h.replace(/./g, (c) => c + c)
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as unknown as Rgb
}

export function rgbToHex(rgb: Rgb): string {
  return (
    '#' +
    rgb
      .map((v) =>
        Math.round(Math.max(0, Math.min(255, v)))
          .toString(16)
          .padStart(2, '0')
      )
      .join('')
  )
}

/** `#abc` / `#AABBCC` → `#aabbcc`. */
export function normalizeHex(hex: string): string {
  return rgbToHex(hexToRgb(hex))
}

/** Relative luminance (WCAG 2.x). */
export function luminance(hex: string): number {
  const weights = [0.2126, 0.7152, 0.0722]
  return hexToRgb(hex).reduce((sum, channel, i) => {
    const v = channel / 255
    const linear = v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
    return sum + linear * weights[i]
  }, 0)
}

export function contrastRatio(a: string, b: string): number {
  const x = luminance(a)
  const y = luminance(b)
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05)
}

/** Linear mix from `a` toward `b` by `t` (0..1), rounded per channel. */
export function mix(a: string, b: string, t: number): string {
  const x = hexToRgb(a)
  const y = hexToRgb(b)
  return rgbToHex(x.map((v, i) => v + (y[i] - v) * t) as unknown as Rgb)
}
